<template>
  <div
    class="ui-toast"
    :class="`ui-toast--${toast.type}`"
    :role="toast.type === 'error' ? 'alert' : 'status'"
    @mouseenter="pause"
    @mouseleave="resume"
    @focusin="pause"
    @focusout="onFocusOut"
  >
    <span class="ui-toast__icon" aria-hidden="true">
      <component :is="icon" :size="20" :stroke-width="2.4" />
    </span>

    <div class="ui-toast__body">
      <p class="ui-toast__title">
        {{ title }}

        <span v-if="toast.count > 1" :key="toast.count" class="ui-toast__count"
          >&times;{{ toast.count }}</span
        >
      </p>

      <p v-if="toast.text" class="ui-toast__text" :title="toast.text">{{ toast.text }}</p>

      <button v-if="toast.action" type="button" class="ui-toast__action" @click="onActionClick">
        {{ actionLabel }}
      </button>
    </div>

    <button
      type="button"
      class="ui-toast__close"
      :aria-label="$t('Close notification')"
      @click="emit('close')"
    >
      <X :size="16" :stroke-width="2.2" aria-hidden="true" />
    </button>

    <span
      v-if="toast.duration !== null"
      :key="toast.restartKey"
      class="ui-toast__timer"
      aria-hidden="true"
      :style="{
        animationDuration: `${toast.duration}ms`,
        animationPlayState: isPaused ? 'paused' : 'running',
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Check, CircleX, Info, TriangleAlert, X } from "lucide-vue-next";
import type { ToastItem } from "@/components/newUi/toast/toastModel";

const props = defineProps<{
  toast: ToastItem;
}>();

const emit = defineEmits<{
  close: [];
  action: [];
}>();

const { t } = useI18n();

const icons = { success: Check, info: Info, warning: TriangleAlert, error: CircleX };

const icon = computed(() => icons[props.toast.type]);

const title = computed(() => {
  if (props.toast.title) {
    return props.toast.title;
  }

  const defaultTitles = {
    success: t("Done"),
    info: t("Information"),
    warning: t("Warning"),
    error: t("Something went wrong"),
  };

  return defaultTitles[props.toast.type];
});

const actionLabel = computed(() => t(props.toast.action?.label ?? ""));

const isPaused = ref(false);

let timeoutId: ReturnType<typeof setTimeout> | null = null;
let startedAt = 0;
let remainingMs = props.toast.duration ?? 0;

const stopTimer = (): void => {
  if (timeoutId !== null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
};

const startTimer = (): void => {
  stopTimer();

  if (props.toast.duration === null) {
    return;
  }

  startedAt = Date.now();
  timeoutId = setTimeout(() => emit("close"), remainingMs);
};

const pause = (): void => {
  if (isPaused.value) {
    return;
  }

  isPaused.value = true;

  if (timeoutId !== null) {
    remainingMs = Math.max(remainingMs - (Date.now() - startedAt), 0);
  }

  stopTimer();
};

const resume = (): void => {
  if (!isPaused.value) {
    return;
  }

  isPaused.value = false;
  startTimer();
};

const onFocusOut = (event: FocusEvent): void => {
  const nextTarget = event.relatedTarget as Node | null;

  if (!nextTarget || !(event.currentTarget as HTMLElement).contains(nextTarget)) {
    resume();
  }
};

const onActionClick = (): void => {
  emit("action");
  emit("close");
};

watch(
  () => props.toast.restartKey,
  () => {
    remainingMs = props.toast.duration ?? 0;

    if (!isPaused.value) {
      startTimer();
    }
  },
);

onMounted(startTimer);

onBeforeUnmount(stopTimer);
</script>
